/**
 * LiveWindow.tsx — 実況ウィンドウ（2026-07-26・開発中）
 *
 * ダイジェスト（記録）の右に常駐する「Gemini が状況を把握して出力する」注意喚起ウィンドウ。
 * 将来は業務マネージャーAIへ発展させる（顧客チャット対応の大部分をAIが管理する構想）。
 *
 * v2（Gemini 生成型）:
 *   トリガー: リクエストウィンドウへの表示（agent.request.created）を受信した各ブラウザが
 *             generate_live_report を呼ぶ → サーバ側で trigger_key + 20秒スロットルで1生成に抑制
 *   生成:     sys/line/api_v2.php が 保留中リクエスト + 直近LINE受信（サーバ側）と
 *             本日の予約スナップショット（このブラウザから送信）を Gemini に渡し、
 *             JSON（summary + alerts[level/time/title/detail]）で判定を返す
 *   配信:     ai_action_logs(action_type='live_report') に記録し、Pusher
 *             （live_report.generated）で全ブラウザの実況を同時更新
 *   手動:     🔄 ボタン（trigger_key='manual:...' はスロットルをバイパス）
 *
 * TODO: タイマー更新（定期的に再生成）/ 15分放置の再アラート — 運用を見て設計
 * 2026-07-26 ver.0 として本番リリース（dev ゲート解除）。
 *
 * 仕様書: md/master_md/feature-digest-window.md（実況セクション）/ agent-rules.md
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isTrainingMode } from '../training/trainingMode';
import { Box, Paper, Typography, Chip, IconButton, CircularProgress, Tooltip } from '@mui/material';
import CampaignIcon from '@mui/icons-material/Campaign';
import RefreshIcon from '@mui/icons-material/Refresh';
import dayjs from 'dayjs';
import { pusherService } from '../services/pusherService';
import { useReservationData } from '../context/ReservationDataContext';
import { LINE_SYNC_KEY, SHARED_OPS_API_ORIGIN } from '../config/instances';
import { isLiveReportEnabled, getStoreKey } from '../config/backend';

// 実況の生成・取得は api_v2.php だが、読むのは agent_requests / ai_action_logs という
// **単一DBの共有テーブル**であり、店舗の出し分けは store パラメータで行う。
// そのため宮古のブラウザもこの那覇ホストに接続するのが正しい。
// ⚠️ getLineApiUrl()(LINE チャット API)と混同しないこと。あちらは那覇 DB 固有の
//    line_users / line_messages を読むので宮古は接続してはいけない。
//
// 経緯: 2026-07-30 までは URL 直書き + ガード無しで、宮古の daylist に那覇の実況が出て、
// かつ宮古の操作が那覇の ai_action_logs と Pusher live_report.generated を発火させていた。
// 2026-08-01 にサーバ側を店舗別化し(agent_requests.store / ai_action_logs.store)、
// 宮古でも自店ぶんの実況を生成できるようにした。
const LIVE_API_URL = (process.env.REACT_APP_LINE_API_URL as string)
    || `${SHARED_OPS_API_ORIGIN}/sys/line/api_v2.php`;
const LIVE_ENABLED = isLiveReportEnabled();
const STORE = getStoreKey();

// 点滅アニメーション（warn アラート用）
const livePulseKeyframes = `
@keyframes livePulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.5); }
  50% { box-shadow: 0 0 0 4px rgba(220, 38, 38, 0.0); }
}`;
if (typeof document !== 'undefined' && !document.getElementById('live-pulse-style')) {
    const style = document.createElement('style');
    style.id = 'live-pulse-style';
    style.textContent = livePulseKeyframes;
    document.head.appendChild(style);
}

interface LiveAlert {
    level: 'warn' | 'watch' | 'ok';
    time?: string;
    title: string;
    detail?: string;
    reservation_id?: string;
}

interface LiveReport {
    summary: string;
    alerts: LiveAlert[];
    createdAt: string | null;
    raw?: string; // JSON パース失敗時の生テキスト表示用
}

const EXCLUDED_RES_STATUSES = new Set(['cancelled', 'nonreserved', 'full', 'done', '4', '5', '6', '2']);

/** Gemini 出力（コードフェンス付きの場合あり）を LiveReport にパース */
const parseReportRaw = (raw: string, createdAt: string | null): LiveReport => {
    const stripped = String(raw || '').replace(/^```(?:json)?\s*/i, '').replace(/\s*```\s*$/, '').trim();
    try {
        const j = JSON.parse(stripped);
        const alerts: LiveAlert[] = Array.isArray(j.alerts)
            ? j.alerts.filter((a: any) => a && a.title).map((a: any) => ({
                level: a.level === 'warn' || a.level === 'watch' || a.level === 'ok' ? a.level : 'watch',
                time: typeof a.time === 'string' ? a.time : undefined,
                title: String(a.title).slice(0, 100),
                detail: a.detail ? String(a.detail).slice(0, 300) : undefined,
                reservation_id: a.reservation_id != null ? String(a.reservation_id) : undefined,
            }))
            : [];
        return { summary: String(j.summary || '').slice(0, 300), alerts, createdAt };
    } catch {
        // JSON でなければ生テキストとして表示（誠実さ優先: 内容を捨てない）
        return { summary: '', alerts: [], createdAt, raw: stripped.slice(0, 800) };
    }
};

// 用語統一（2026-07-26）: 赤=警告(warn) / 黄=注意(watch) / 緑=対応済み(ok)
const LEVEL_STYLE: Record<LiveAlert['level'], { bg: string; border: string; label: string; labelColor: string }> = {
    warn: { bg: '#fee2e2', border: '#dc2626', label: '⚠️ 警告', labelColor: '#dc2626' },
    watch: { bg: '#fef9c3', border: '#ca8a04', label: '⚠ 注意', labelColor: '#a16207' },
    ok: { bg: '#dcfce7', border: '#16a34a', label: '✓ 対応済み', labelColor: '#16a34a' },
};

interface LiveWindowProps {
    lineUsers?: any[];
    /** 予約ステータスマスタ（id→日本語名の解決に使用。数値コードを Gemini に渡さない） */
    reservationStatusList?: any[];
    /** 車両ステータスマスタ（迎え中/送り中の判定材料として日本語名で渡す） */
    vehicleStatusList?: any[];
    /** Paper 幅（既定 300。モバイルPWA のボトムシート内では '100%' を渡す） */
    width?: number | string;
    /** 本文の高さ px（既定 5行=135。モバイルPWA では大きめを渡す） */
    bodyHeight?: number;
}

const LiveWindow: React.FC<LiveWindowProps> = ({ reservationStatusList, vehicleStatusList, width = 300, bodyHeight = 5 * 27 }) => {
    const [report, setReport] = useState<LiveReport | null>(null);
    const [generating, setGenerating] = useState(false);
    const { reservations, vehicles } = useReservationData() as any;
    // 1分ごとに再評価（貸出時刻超過の判定用）
    const [nowTick, setNowTick] = useState(() => dayjs());
    useEffect(() => {
        const id = setInterval(() => setNowTick(dayjs()), 60 * 1000);
        return () => clearInterval(id);
    }, []);

    // 予約ステータス id → 日本語名（agent-rules 固定ルール4: 内部IDを出さない）
    const resStatusLabelById = useMemo(() => {
        const map: Record<string, string> = {};
        (reservationStatusList || []).forEach((s: any) => { if (s?.id != null) map[String(s.id)] = s.label || ''; });
        return map;
    }, [reservationStatusList]);

    // 車両ステータス id → 日本語名（「迎え中/送り中=対応進行中」の判定材料）
    const vehStatusLabelById = useMemo(() => {
        const map: Record<string, string> = {};
        (vehicleStatusList || []).forEach((s: any) => { if (s?.id != null) map[String(s.id)] = s.label || ''; });
        return map;
    }, [vehicleStatusList]);

    // ---- 本日の予約スナップショット（Gemini に渡すコンテキスト）----
    const buildContext = useCallback(() => {
        const today = dayjs().format('YYYY-MM-DD');
        const pick = (r: any, kind: 'departure' | 'return') => {
            let jd: any = r.json_data ?? r.detail?.json_data;
            if (typeof jd === 'string') { try { jd = JSON.parse(jd); } catch { jd = null; } }
            const v = r.vehicle_id != null ? (vehicles || {})[String(r.vehicle_id)] : null;
            return {
                kind,
                reservation_id: String(r.id),
                customer_name: r.customer_name || '',
                time: kind === 'departure' ? dayjs(r.start_date).format('HH:mm') : dayjs(r.end_date).format('HH:mm'),
                // 日本語ステータス名で渡す（"3" 等の数値コードでは Gemini が利用中と判定できず
                // 誤警告になる事故があった 2026-07-26。マスタ未取得時のみ生値フォールバック）
                status: resStatusLabelById[String(r.status)] || String(r.status),
                vehicle_code: v?.vehicle_code || null,
                // 車両の現ステータス（日本語）。「迎え中/送り中」なら送迎進行中の判定材料になる
                vehicle_status: v?.status != null ? (vehStatusLabelById[String(v.status)] || null) : null,
                arrival_contact: jd?.data?.['到着連絡'] || null,
                airport_subzone: jd?.data?.['空港'] || null,
            };
        };
        const list: any[] = [];
        (Array.isArray(reservations) ? reservations : []).forEach((r: any) => {
            if (EXCLUDED_RES_STATUSES.has(String(r.status))) return;
            if (r.start_date && String(r.start_date).substring(0, 10) === today) {
                // 利用中(3)の貸出は出発済み = 本日の対応は完了。注意対象から除外
                // （到着連絡の残存データで誤警告になるため。2026-07-26 金テヨン様事例）
                if (String(r.status) === '3') return;
                list.push(pick(r, 'departure'));
            } else if (r.end_date && String(r.end_date).substring(0, 10) === today) {
                list.push(pick(r, 'return'));
            }
        });
        return {
            now: dayjs().format('YYYY-MM-DD HH:mm'),
            today_reservations: list.slice(0, 80),
            // 貸出時刻超過（コード計算済み）。Gemini は summary で触れてよいが一覧表示はシステム側
            overdue_departures: overdueRef.current,
        };
    }, [reservations, vehicles, resStatusLabelById, vehStatusLabelById]);

    // ---- 貸出時刻超過（決定的ロジック・Gemini 非依存）----
    // 予約確定(booked)のまま貸出時刻を過ぎた予約者を列挙。お客様への「大丈夫ですか」連絡の判断材料。
    // 名前の列挙は捏造リスクを避けるため LLM に任せずコードで計算し、常時表示する。
    const overdueDepartures = useMemo(() => {
        const today = nowTick.format('YYYY-MM-DD');
        const list: { name: string; time: string }[] = [];
        (Array.isArray(reservations) ? reservations : []).forEach((r: any) => {
            if (String(r.status) !== '1') return; // 予約確定(booked)のみ = 未アクション
            if (!r.start_date || String(r.start_date).substring(0, 10) !== today) return;
            const start = dayjs(r.start_date);
            if (start.isValid() && start.isBefore(nowTick)) {
                list.push({ name: r.customer_name || '不明', time: start.format('HH:mm') });
            }
        });
        return list.sort((a, b) => a.time.localeCompare(b.time));
    }, [reservations, nowTick]);
    const overdueRef = useRef(overdueDepartures);
    useEffect(() => { overdueRef.current = overdueDepartures; }, [overdueDepartures]);
    const buildContextRef = useRef(buildContext);
    useEffect(() => { buildContextRef.current = buildContext; }, [buildContext]);

    // ---- レポート生成（サーバ側で重複抑制）----
    const generate = useCallback(async (triggerKey: string) => {
        if (!LIVE_ENABLED) return;
        // トレーニングモード中は生成しない（Gemini課金・本番ログ汚染を避ける。2026-08-24）
        if (isTrainingMode()) return;
        setGenerating(true);
        try {
            const res = await fetch(`${LIVE_API_URL}?action=generate_live_report&api_key=${LINE_SYNC_KEY}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ trigger_key: triggerKey, context: buildContextRef.current(), store: STORE }),
            });
            const json = await res.json();
            if (json.success && json.report_raw) {
                setReport(parseReportRaw(json.report_raw, json.created_at || null));
            }
        } catch { /* Pusher 配信 or 次回トリガーで回復 */ }
        finally { setGenerating(false); }
    }, []);

    // ---- 初期表示: 本日の最新レポート ----
    useEffect(() => {
        if (!LIVE_ENABLED) return;
        // トレーニング中は本番の実況（実在のお客様の話）を出さない
        if (isTrainingMode()) return;
        (async () => {
            try {
                const res = await fetch(`${LIVE_API_URL}?action=get_latest_live_report&api_key=${LINE_SYNC_KEY}`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ store: STORE }),
                });
                const json = await res.json();
                if (json.success && json.data?.report_raw) {
                    setReport(parseReportRaw(json.data.report_raw, json.data.created_at || null));
                }
            } catch { /* 初期表示なしでもトリガーで生成される */ }
        })();
    }, []);

    // ---- トリガー購読 ----
    useEffect(() => {
        if (!LIVE_ENABLED) return;
        const unsubscribe = pusherService.subscribe((eventName: string, data: any) => {
            // リクエストウィンドウ表示 → 実況を再生成（複数ブラウザはサーバ側で1生成に抑制。
            // 0〜1.5秒のジッターで同時着火も分散）
            if (eventName === 'agent.request.created' && data?.id) {
                const delay = Math.floor(Math.random() * 1500);
                setTimeout(() => generate(`req:${data.id}`), delay);
            }
            // 他ブラウザ（または自分）が生成したレポートの配信
            if (eventName === 'live_report.generated' && data?.report_raw) {
                setReport(parseReportRaw(data.report_raw, data.created_at || null));
            }
        });
        return unsubscribe;
    }, [generate]);

    // 実況が無効な店舗では実況そのものを出さない。
    // ※ 呼び出し側(DayListReservations / MobileDayList)でも isLineEnabled() でガードしているが、
    //    将来の追加箇所で漏れないよう、コンポーネント内でも最終防衛する。
    //    hooks 呼び出し後に return するので hooks の順序は変わらない(LIVE_ENABLED は定数)。
    if (!LIVE_ENABLED) return null;

    const warnCount = report?.alerts.filter(a => a.level === 'warn').length || 0;

    return (
        <Paper variant="outlined" sx={{ width, borderRadius: 1.5, overflow: 'hidden', flexShrink: 0, display: 'flex', flexDirection: 'column' }}>
            {/* ヘッダー */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 0.75, py: 0.1, bgcolor: warnCount > 0 ? '#b91c1c' : '#64748b', color: '#fff' }}>
                <CampaignIcon sx={{ fontSize: 14 }} />
                <Typography sx={{ fontSize: 11, fontWeight: 700, flex: 1 }}>実況</Typography>
                {warnCount > 0 && (
                    <Chip label={`警告 ${warnCount}`} size="small" sx={{ height: 16, fontSize: 9, bgcolor: '#fff', color: '#b91c1c', fontWeight: 700 }} />
                )}
                {report?.createdAt && (
                    <Typography sx={{ fontSize: 9, opacity: 0.85 }}>{dayjs(report.createdAt).format('HH:mm')}</Typography>
                )}
                <Tooltip title="実況を手動更新（Gemini 再生成）">
                    <IconButton size="small" sx={{ color: '#fff', p: 0.25 }} disabled={generating}
                        onClick={() => generate(`manual:${Math.random().toString(36).slice(2)}`)}>
                        {generating ? <CircularProgress size={12} sx={{ color: '#fff' }} /> : <RefreshIcon sx={{ fontSize: 13 }} />}
                    </IconButton>
                </Tooltip>
            </Box>

            {/* 本文（ダイジェストと同じ高さ = 5行ぶん） */}
            <Box sx={{ overflowY: 'auto', height: bodyHeight, px: 0.75, py: 0.5, bgcolor: '#fafaf9' }}>
                {/* 貸出時刻超過（決定的ロジック・常設。Gemini 非依存なので常に正確） */}
                {overdueDepartures.length > 0 && (
                    <Box sx={{ mb: 0.5, px: 0.75, py: 0.4, borderRadius: 1, bgcolor: '#ffedd5', borderLeft: '3px solid #ea580c' }}>
                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#9a3412' }}>
                            ⏰ 貸出時刻を過ぎた予約者({overdueDepartures.length})
                        </Typography>
                        <Typography sx={{ fontSize: 10.5, lineHeight: 1.5, wordBreak: 'break-word' }}>
                            {overdueDepartures.map(o => `${o.time} ${o.name} 様`).join('、')}
                        </Typography>
                        <Typography sx={{ fontSize: 9.5, color: '#9a3412' }}>
                            必要ならお客様へ「ご予定は大丈夫ですか」の連絡を検討
                        </Typography>
                    </Box>
                )}
                {!report && overdueDepartures.length === 0 && (
                    <Typography sx={{ fontSize: 11, color: '#94a3b8', textAlign: 'center', py: 1.5 }}>
                        {generating ? 'Gemini が状況を確認中…' : 'リクエスト受信時に Gemini が実況を生成します'}
                    </Typography>
                )}
                {report?.summary && (
                    <Typography sx={{ fontSize: 10.5, color: '#475569', mb: 0.5, lineHeight: 1.4 }}>
                        {report.summary}
                    </Typography>
                )}
                {report?.raw && (
                    <Typography sx={{ fontSize: 10.5, whiteSpace: 'pre-wrap', lineHeight: 1.4 }}>{report.raw}</Typography>
                )}
                {report?.alerts.map((a, i) => {
                    const s = LEVEL_STYLE[a.level];
                    return (
                        <Box key={i} sx={{
                            mb: 0.5, px: 0.75, py: 0.4, borderRadius: 1,
                            bgcolor: s.bg, borderLeft: `3px solid ${s.border}`,
                            animation: a.level === 'warn' ? 'livePulse 1.6s ease-in-out infinite' : 'none',
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {a.time && <Typography sx={{ fontSize: 10, color: '#64748b', fontWeight: 600 }}>{a.time}</Typography>}
                                <Typography sx={{ fontSize: 11, fontWeight: 700, flex: 1 }}>{a.title}</Typography>
                                <Typography sx={{ fontSize: 9.5, fontWeight: 700, color: s.labelColor, whiteSpace: 'nowrap' }}>{s.label}</Typography>
                            </Box>
                            {a.detail && (
                                <Typography sx={{ fontSize: 10.5, lineHeight: 1.4, wordBreak: 'break-word' }}>{a.detail}</Typography>
                            )}
                        </Box>
                    );
                })}
            </Box>
        </Paper>
    );
};

export default LiveWindow;
